import { admin2 } from './instance'

const baseUrl = (shopId) => `/${shopId}/ticketBoxRecord`

/**
 * 新增票匣紀錄（發放給會員）
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {string} params.memberId - 會員 ID（必填）
 * @param {string} params.ticketBoxId - 票匣 ID（必填）
 * @param {string} [params.note] - 備註
 * @param {number} params.availableTimes - 可使用次數（min: 1，必填）
 * @returns {Promise} 新增結果
 */
export const CreateTicketBoxRecord = async ({
  shopId,
  memberId,
  ticketBoxId,
  note,
  availableTimes,
}) => {
  return await admin2({
    method: 'post',
    url: baseUrl(shopId),
    data: {
      memberId,
      ticketBoxId,
      note,
      availableTimes,
    },
  })
}

/**
 * 查詢票匣紀錄列表
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {string} [params.code] - 票匣編號（模糊搜尋）
 * @param {string} [params.name] - 票匣名稱（模糊搜尋）
 * @param {string} [params.status] - 使用狀態：open / available / complete / overdue / voided / all
 * @param {string} [params.MemberId] - 會員 ID
 * @param {string} [params.startAt] - 日期範圍起始（ISO datetime）
 * @param {string} [params.endAt] - 日期範圍結束（ISO datetime）
 * @param {string} [params.sort] - 排序依據：createdAt / exp，預設 createdAt
 * @param {string} [params.dateRangeType] - 日期範圍對應欄位：createdAt / exp
 * @param {number} [params.start=0] - 分頁起始
 * @param {number} [params.limit=10] - 每頁筆數
 * @returns {Promise} 票匣紀錄列表
 */
export const GetTicketBoxRecord = async ({
  shopId,
  code,
  name,
  status,
  MemberId,
  startAt,
  endAt,
  sort,
  dateRangeType,
  start,
  limit,
}) => {
  return await admin2({
    method: 'get',
    url: baseUrl(shopId),
    params: {
      code,
      name,
      status,
      MemberId,
      startAt,
      endAt,
      sort,
      dateRangeType,
      start,
      limit,
    },
  })
}

/**
 * 查詢票匣紀錄數量
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {string} [params.code] - 票匣編號（模糊搜尋）
 * @param {string} [params.name] - 票匣名稱（模糊搜尋）
 * @param {string} [params.status] - 使用狀態
 * @param {string} [params.MemberId] - 會員 ID
 * @param {string} [params.startAt] - 日期範圍起始
 * @param {string} [params.endAt] - 日期範圍結束
 * @param {string} [params.dateRangeType] - 日期範圍對應欄位
 * @returns {Promise} 紀錄數量
 */
export const GetTicketBoxRecordCount = async ({
  shopId,
  code,
  name,
  status,
  MemberId,
  startAt,
  endAt,
  dateRangeType,
}) => {
  return await admin2({
    method: 'get',
    url: `${baseUrl(shopId)}/count`,
    params: {
      code,
      name,
      status,
      MemberId,
      startAt,
      endAt,
      dateRangeType,
    },
  })
}

/**
 * 查詢單一票匣紀錄
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {string} params.id - 票匣紀錄 ID
 * @returns {Promise} 票匣紀錄詳情（含 TicketBoxRecordUses、TicketBox）
 */
export const FindTicketBoxRecord = async ({ shopId, id }) => {
  return await admin2({
    method: 'get',
    url: `${baseUrl(shopId)}/${id}`,
  })
}

/**
 * 核銷票匣紀錄
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {string} params.id - 票匣紀錄 ID
 * @param {number} params.times - 核銷次數（min: 1，必填）
 * @returns {Promise} 核銷結果
 */
export const RedeemTicketBoxRecord = async ({ shopId, id, times }) => {
  return await admin2({
    method: 'post',
    url: `${baseUrl(shopId)}/${id}/redeem`,
    data: {
      times,
    },
  })
}

/**
 * 作廢票匣紀錄
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {string} params.id - 票匣紀錄 ID
 * @returns {Promise} 作廢結果
 */
export const VoidTicketBoxRecord = async ({ shopId, id }) => {
  return await admin2({
    method: 'post',
    url: `${baseUrl(shopId)}/${id}/void`,
    data: {},
  })
}

/**
 * 匯出票匣新增紀錄
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {Object} [params.input] - 篩選條件
 * @param {string} [params.input.MemberId] - 會員 ID
 * @param {string} [params.input.sort] - 排序依據
 * @param {string} [params.input.status] - 使用狀態
 * @param {string} [params.input.dateRangeType] - 日期範圍對應欄位
 * @param {string} [params.input.startAt] - 日期範圍起始
 * @param {string} [params.input.endAt] - 日期範圍結束
 * @param {string} [params.input.code] - 票匣編號
 * @param {string} [params.input.name] - 票匣名稱
 * @returns {Promise} { msg: 'ok' }
 */
export const CreateTicketBoxRecordExportCreated = async ({ shopId, input }) => {
  return await admin2({
    method: 'post',
    url: `${baseUrl(shopId)}/createSheetExportTaskCreatedBasic`,
    data: {
      input,
    },
  })
}

/**
 * 匯出票匣使用紀錄
 * @param {Object} params
 * @param {string} params.shopId - 店家 ID
 * @param {Object} [params.input] - 篩選條件
 * @param {string} [params.input.MemberId] - 會員 ID
 * @param {string} [params.input.sort] - 排序依據
 * @param {string} [params.input.status] - 使用狀態
 * @param {string} [params.input.dateRangeType] - 日期範圍對應欄位
 * @param {string} [params.input.startAt] - 日期範圍起始
 * @param {string} [params.input.endAt] - 日期範圍結束
 * @param {string} [params.input.code] - 票匣編號
 * @param {string} [params.input.name] - 票匣名稱
 * @returns {Promise} { msg: 'ok' }
 */
export const CreateTicketBoxRecordExportUsed = async ({ shopId, input }) => {
  return await admin2({
    method: 'post',
    url: `${baseUrl(shopId)}/createSheetExportTaskUsedBasic`,
    data: {
      input,
    },
  })
}
